import { useState, type FormEvent } from 'react';
import {
  Alert, AlertTitle, Autocomplete, Box, Button, Chip, CircularProgress, Collapse, Dialog,
  DialogActions, DialogContent, DialogTitle, Grid, IconButton, MenuItem, Paper, Select, Stack,
  TextField, Tooltip, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MovieIcon from '@mui/icons-material/Movie';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { urlPoster } from '../lib/eventos.js';
import type { ApiFallo, BusquedaAvanzada, ResultadoBusqueda } from '../lib/api.js';

/** Lo que el diálogo necesita saber de la obra que se vincula: de dónde parte el
 *  formulario y qué ficha, si alguna, tiene ya. Lo mismo vale para un elemento de la
 *  watchlist y para una película de la biblioteca (D-28). */
export interface ObraAVincular {
  titulo: string;
  anio: number | null;
  hispashareId: number | null;
}

/** Lo que devuelve quien vincula: si el IMDb de la ficha elegida no era el que se traía,
 *  el diálogo se queda abierto y lo dice. */
export interface ResultadoVinculacion {
  imdbDiscrepa: boolean;
  titulo: string;
  imdbId: string;
}

/** Vincular a mano un elemento de la watchlist —o una película de la biblioteca— con su
 *  ficha de HispaShare.
 *
 *  El cruce automático NO vincula por parecido de título: busca, abre la ficha candidata y
 *  compara el IMDb. Cuando la ficha no lo trae, lo trae mal, o el título castellano no se
 *  parece en nada al inglés de tu watchlist, se planta a propósito —vincular mal acaba
 *  descargando otra película—. Esto es la salida: la búsqueda avanzada del propio sitio, con
 *  todos sus filtros, y la ficha la elige quien está mirando. */

/** Las categorías del sitio. Solo `Películas` sabe descargar la app, pero se ofrecen todas:
 *  quien busca a mano quiere ver lo mismo que vería en HispaShare. */
const CATEGORIAS = ['Películas', 'Series', 'Documentales', 'Música'];

/** Los géneros de películas, leídos de los propios enlaces `genre=1.x` del sitio. NO es una
 *  lista cerrada —`advgen` admite cualquier texto—, así que el campo sugiere estos y deja
 *  escribir otro. */
const GENEROS = [
  'Acción', 'Animación', 'Aventura', 'Bélico', 'Biografía', 'Ciencia ficción', 'Comedia',
  'Crimen', 'Deportes', 'Drama', 'Familiar', 'Fantástico', 'Historia', 'Misterio', 'Musical',
  'Romántico', 'Terror', 'Thriller', 'Venganza',
];

/** El formulario guarda texto, no números: un campo de año a medio escribir es «19», que no
 *  es un año pero sí un estado legítimo mientras se teclea. La conversión ocurre al buscar. */
interface Formulario {
  titulo: string;
  anioMin: string;
  anioMax: string;
  pais: string;
  categorias: string[];
  generos: string[];
  duracionMin: string;
  duracionMax: string;
  director: string;
  actor: string;
  sinopsis: string;
  calidad: string;
  formato: string;
  video: string;
  audio: string;
  origen: string;
  ripper: string;
  notaMin: string;
  notaMax: string;
}

const VACIO: Formulario = {
  titulo: '', anioMin: '', anioMax: '', pais: '', categorias: [], generos: [],
  duracionMin: '', duracionMax: '', director: '', actor: '', sinopsis: '',
  calidad: '', formato: '', video: '', audio: '', origen: '', ripper: '',
  notaMin: '', notaMax: '',
};

/** Lo que se sabe del elemento, ya puesto en el formulario: el título con el que se buscó y
 *  su año con un margen de ±1, que es el que absorbe la diferencia habitual entre año de
 *  producción y de estreno. Categoría en «Películas» porque es lo que la app descarga. */
function inicial(e: ObraAVincular): Formulario {
  return {
    ...VACIO,
    titulo: e.titulo,
    anioMin: e.anio ? String(e.anio - 1) : '',
    anioMax: e.anio ? String(e.anio + 1) : '',
    categorias: ['Películas'],
  };
}

const texto = (v: string) => v.trim() || undefined;
const numero = (v: string) => {
  const n = Number(v.trim());
  return v.trim() && Number.isFinite(n) ? n : undefined;
};

function construirFiltros(f: Formulario): BusquedaAvanzada {
  return {
    titulo: texto(f.titulo),
    anioMin: numero(f.anioMin),
    anioMax: numero(f.anioMax),
    pais: texto(f.pais),
    categorias: f.categorias.length ? f.categorias : undefined,
    generos: f.generos.length ? f.generos : undefined,
    duracionMin: numero(f.duracionMin),
    duracionMax: numero(f.duracionMax),
    director: texto(f.director),
    actor: texto(f.actor),
    sinopsis: texto(f.sinopsis),
    calidad: texto(f.calidad),
    formato: texto(f.formato),
    video: texto(f.video),
    audio: texto(f.audio),
    origen: texto(f.origen),
    ripper: texto(f.ripper),
    notaMin: numero(f.notaMin),
    notaMax: numero(f.notaMax),
  };
}

/** Sin un solo filtro, HispaShare devuelve su portada de novedades: parecería un resultado
 *  y no lo es. El botón se queda apagado hasta que haya algo por lo que buscar. */
const hayAlgoQueBuscar = (f: Formulario) =>
  Object.values(construirFiltros(f)).some((v) => v !== undefined);

function Resultado({
  r, yaVinculada, onElegir, ocupado,
}: {
  r: ResultadoBusqueda;
  yaVinculada: boolean;
  onElegir: () => void;
  ocupado: boolean;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
        <Box
          sx={{
            width: 54, minWidth: 54, aspectRatio: '2/3', borderRadius: 1, overflow: 'hidden',
            bgcolor: 'action.disabledBackground', display: 'grid', placeItems: 'center',
          }}
        >
          {urlPoster(r.posterUrl) ? (
            <Box
              component="img"
              src={urlPoster(r.posterUrl)!}
              alt=""
              loading="lazy"
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <MovieIcon fontSize="small" sx={{ color: 'text.disabled' }} />
          )}
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {r.title}
            {r.year && (
              <Typography component="span" color="text.secondary">
                {' '}
                ({r.year})
              </Typography>
            )}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {[
              r.director,
              // La categoría delante de todo: el buscador mezcla series y películas, y
              // vincular una serie a una película de la watchlist es el error caro.
              r.category,
              r.genres.length ? r.genres.join(' / ') : null,
              r.imdbRating != null ? `${r.imdbRating}` : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </Typography>
          {r.synopsisCorta && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                overflow: 'hidden', mt: 0.5,
              }}
            >
              {r.synopsisCorta}
            </Typography>
          )}
        </Box>

        <Stack spacing={1} sx={{ alignItems: 'flex-end' }}>
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
            {/* La ficha, en otra pestaña: si con el título y el año no basta para estar
                seguro, lo que se mira son sus versiones. El diálogo se queda como está. */}
            <Tooltip title="Ver la ficha en otra pestaña">
              <IconButton
                size="small"
                component="a"
                href={`/pelicula/${r.id}`}
                target="_blank"
                rel="noreferrer"
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button size="small" variant="contained" disabled={ocupado || yaVinculada} onClick={onElegir}>
              Es esta
            </Button>
          </Stack>
          {yaVinculada && <Chip size="small" label="Vinculada ahora" />}
        </Stack>
      </Stack>
    </Paper>
  );
}

export function DialogoVincular({
  item,
  vincular: hacerVinculo,
  onCerrar,
}: {
  item: ObraAVincular;
  /** Quien sabe con qué se vincula: la watchlist o la revisión. Invalidar lo suyo es cosa
   *  de quien llama. */
  vincular: (hispashareId: string) => Promise<ResultadoVinculacion>;
  onCerrar: () => void;
}) {
  const [f, setF] = useState<Formulario>(() => inicial(item));
  const [masFiltros, setMasFiltros] = useState(false);
  // Los filtros con los que se buscó, que no son los del formulario: teclear no dispara
  // peticiones a HispaShare.
  const [consulta, setConsulta] = useState<BusquedaAvanzada | null>(null);
  const [discrepancia, setDiscrepancia] = useState<{ titulo: string; imdb: string } | null>(null);

  const cambiar = <K extends keyof Formulario>(clave: K, valor: Formulario[K]) =>
    setF((previo) => ({ ...previo, [clave]: valor }));

  const busqueda = useInfiniteQuery({
    queryKey: ['buscar-avanzada', consulta],
    queryFn: ({ pageParam }) => api.buscarAvanzada({ ...consulta, pagina: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (ultima, todas) => {
      if (!ultima.hayMas) return undefined;
      // La trampa del sitio: pedir una página que no existe devuelve otra vez la última, no
      // una lista vacía. Se para cuando una página no trae ningún id nuevo.
      const previos = new Set(todas.slice(0, -1).flatMap((p) => p.items.map((i) => i.id)));
      return ultima.items.some((i) => !previos.has(i.id)) ? todas.length + 1 : undefined;
    },
    enabled: consulta !== null,
  });

  const vincular = useMutation({
    mutationFn: (hispashareId: string) => hacerVinculo(hispashareId),
    onSuccess: (r) => {
      // Lo normal es cerrar: ya está hecho y no hay nada que contar. Con el IMDb en
      // desacuerdo el diálogo se queda abierto, porque eso es justo lo que hizo desistir al
      // cruce automático y quien vinculó tiene que enterarse.
      if (r.imdbDiscrepa) setDiscrepancia({ titulo: r.titulo, imdb: r.imdbId });
      else onCerrar();
    },
  });

  const buscar = (e: FormEvent) => {
    e.preventDefault();
    if (hayAlgoQueBuscar(f)) setConsulta(construirFiltros(f));
  };

  // Aplanadas y sin repetidos: dos páginas pueden traer la misma película cuando la última
  // se pide dos veces, y React no perdona dos claves iguales.
  const vistos = new Set<string>();
  const resultados = (busqueda.data?.pages ?? [])
    .flatMap((p) => p.items)
    .filter((r) => !vistos.has(r.id) && vistos.add(r.id));

  const fallo = busqueda.error as ApiFallo | null;
  const falloVincular = vincular.error as ApiFallo | null;

  const campo = (clave: keyof Formulario, etiqueta: string, extra?: object) => (
    <TextField
      size="small"
      fullWidth
      label={etiqueta}
      value={f[clave] as string}
      onChange={(e) => cambiar(clave, e.target.value as never)}
      {...extra}
    />
  );

  return (
    <Dialog open onClose={onCerrar} fullWidth maxWidth="md">
      <DialogTitle>
        Vincular «{item.titulo}» con su ficha
        <Typography variant="body2" color="text.secondary">
          El cruce automático compara el IMDb y se planta cuando no coincide. Aquí eliges tú:
          la ficha que marques se queda.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <Box component="form" onSubmit={buscar}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>{campo('titulo', 'Título')}</Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                {campo('anioMin', 'Año desde', { type: 'number' })}
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                {campo('anioMax', 'Año hasta', { type: 'number' })}
              </Grid>
            </Grid>

            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mt: 2 }}>
              <Button
                size="small"
                onClick={() => setMasFiltros((v) => !v)}
                endIcon={
                  <ExpandMoreIcon
                    sx={{ transform: masFiltros ? 'rotate(180deg)' : 'none', transition: '0.2s' }}
                  />
                }
              >
                {masFiltros ? 'Menos filtros' : 'Más filtros'}
              </Button>
              <Box sx={{ flex: 1 }} />
              <Button size="small" onClick={() => setF(VACIO)}>
                Limpiar
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SearchIcon />}
                disabled={!hayAlgoQueBuscar(f) || busqueda.isFetching}
              >
                Buscar
              </Button>
            </Stack>

            {/* Todo lo que admite `?view=advsearch`, con los nombres del propio sitio. Los
                seis últimos filtran por la VERSIÓN, no por la película: sirven para dar con
                una ficha concreta cuando el título se repite. */}
            <Collapse in={masFiltros}>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid size={{ xs: 12, sm: 6 }}>{campo('director', 'Director')}</Grid>
                <Grid size={{ xs: 12, sm: 6 }}>{campo('actor', 'Reparto')}</Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Select
                    size="small"
                    fullWidth
                    multiple
                    displayEmpty
                    value={f.categorias}
                    onChange={(e) =>
                      cambiar('categorias', typeof e.target.value === 'string' ? [e.target.value] : e.target.value)
                    }
                    renderValue={(v) => (v.length ? v.join(', ') : 'Categoría: todas')}
                  >
                    {CATEGORIAS.map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Autocomplete
                    multiple
                    freeSolo
                    size="small"
                    options={GENEROS}
                    value={f.generos}
                    onChange={(_e, v) => cambiar('generos', v)}
                    renderInput={(params) => <TextField {...params} label="Géneros" />}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>{campo('pais', 'País')}</Grid>
                <Grid size={{ xs: 12, sm: 6 }}>{campo('sinopsis', 'La sinopsis contiene')}</Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  {campo('duracionMin', 'Duración mín. (min)', { type: 'number' })}
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  {campo('duracionMax', 'Duración máx. (min)', { type: 'number' })}
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  {campo('notaMin', 'Nota mín.', { type: 'number' })}
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  {campo('notaMax', 'Nota máx.', { type: 'number' })}
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>{campo('calidad', 'Calidad')}</Grid>
                <Grid size={{ xs: 12, sm: 4 }}>{campo('formato', 'Formato')}</Grid>
                <Grid size={{ xs: 12, sm: 4 }}>{campo('video', 'Códec de vídeo')}</Grid>
                <Grid size={{ xs: 12, sm: 4 }}>{campo('audio', 'Códec de audio')}</Grid>
                <Grid size={{ xs: 12, sm: 4 }}>{campo('origen', 'Origen')}</Grid>
                <Grid size={{ xs: 12, sm: 4 }}>{campo('ripper', 'Ripper')}</Grid>
              </Grid>
            </Collapse>
          </Box>

          {discrepancia && (
            <Alert severity="warning" onClose={() => setDiscrepancia(null)}>
              <AlertTitle>Vinculada, pero el IMDb no coincidía</AlertTitle>
              «{discrepancia.titulo}» tiene en HispaShare un IMDb distinto del que traía tu
              watchlist ({discrepancia.imdb}). Es exactamente lo que hizo que el cruce
              automático no la vinculara solo; se ha respetado tu elección y se ha conservado
              el IMDb de tu watchlist.
            </Alert>
          )}

          {falloVincular && (
            <Alert severity="error" onClose={() => vincular.reset()}>
              {falloVincular.message}
            </Alert>
          )}

          {/* Un error NUNCA se pinta como «no hay resultados»: es la diferencia entre
              encogerse de hombros y saber que hay algo roto. */}
          {fallo && (
            <Alert severity={fallo.code === 'indexer_unconfigured' ? 'warning' : 'error'}>
              <AlertTitle>
                {fallo.code === 'indexer_unconfigured'
                  ? 'HispaShare no está configurado'
                  : 'La búsqueda ha fallado'}
              </AlertTitle>
              {fallo.message}
            </Alert>
          )}

          {busqueda.isFetching && !busqueda.isFetchingNextPage && <CircularProgress size={28} />}

          {consulta && !fallo && !busqueda.isFetching && resultados.length === 0 && (
            <Alert severity="info">
              Sin resultados con esos filtros. Prueba a quitar el año —hay fichas que lo tienen
              mal puesto— o a recortar el título por los dos puntos: «X-Men: First Class» no
              devuelve nada y «X-Men» sí.
            </Alert>
          )}

          {resultados.map((r) => (
            <Resultado
              key={r.id}
              r={r}
              yaVinculada={item.hispashareId != null && String(item.hispashareId) === r.id}
              ocupado={vincular.isPending}
              onElegir={() => vincular.mutate(r.id)}
            />
          ))}

          {busqueda.hasNextPage && (
            <Button
              onClick={() => void busqueda.fetchNextPage()}
              disabled={busqueda.isFetchingNextPage}
            >
              {busqueda.isFetchingNextPage ? 'Trayendo más…' : 'Ver más resultados'}
            </Button>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCerrar}>{discrepancia ? 'Cerrar' : 'Cancelar'}</Button>
      </DialogActions>
    </Dialog>
  );
}
